// mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
import { Injectable } from '@angular/core';

/**
 * Tema dinámico por compañía.
 *
 * El login devuelve 3 tonos (PRIMARIO, CLARO, MUY_CLARO) y este servicio los
 * aplica como variables CSS (--app-*) en todo el sitio, con la regla 60-30-10:
 *   - Superficies (fondos, tarjetas, filas): grises neutros derivados del PRIMARIO.
 *   - Marca (menú, encabezados, botón principal): PRIMARIO y sus tonos oscuros.
 *   - Acento (foco, links, selección): CLARO y MUY_CLARO.
 *
 * mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
 * El tema llega del servicio PermisoInicioSesion (campo TEMA). Si llega "###"
 * se usan los colores originales del sistema.
 * Mientras el backend no envíe el tema se usa PRE-DETERMINADO.  (ya no aplica)
 */

export interface Tema {
  PRIMARIO: string;   // color principal (menú, encabezados, botón principal)
  CLARO: string;      // color de acento (foco, links, botones secundarios)
  MUY_CLARO: string;  // tono suave para selección y hover (filas, opciones de combos)
}

// mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
// Se retira el tema de prueba (predeterminado): el tema ya llega del servicio
// PermisoInicioSesion (campo TEMA). Se deja comentado como referencia.
// -----------------------------------------------------------------------------
// PREDERMINADO: cambiar según respuesta del servicio.
// -----------------------------------------------------------------------------
// // El tema ya llega del servicio PermisoInicioSesion (campo TEMA); con true, los
// // demás servicios del login pisarían los colores originales cuando llega "###".
// // export const USAR_TEMA = true;
// export const USAR_TEMA = false;
//
// export const TEMA: Tema = {
//   PRIMARIO:  '#2e6573',   // menú, footer, encabezados de grilla
//   CLARO:     '#00afac',   // acentos (Buscar/Limpiar, tab activo, foco, links)
//   MUY_CLARO: '#e6f6f5',   // fila/opción seleccionada y hover
// };

// Límites para que el color principal no canse la vista en superficies grandes
// (encabezados de grilla, barras, botones). Si el color que llega del login es
// muy oscuro, muy claro o muy saturado, se ajusta a este rango manteniendo el tono.
const PRIMARIO_LUZ_MIN = 0.30;   // 0 = negro, 1 = blanco
const PRIMARIO_LUZ_MAX = 0.46;
const PRIMARIO_SAT_MAX = 0.65;   // 0 = gris, 1 = color puro

// mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
// Intensidad del color en las filas alternas de las grillas (0.11 = 11 %).
// 0.11 da la misma intensidad que tenía el gris original; bajar a 0.05-0.07 si se ve fuerte.
const FILA_TINTE = 0.11;

const STORAGE_KEY = 'app_tema';
const HEX = /^#[0-9a-fA-F]{6}$/;

@Injectable({ providedIn: 'root' })
export class ThemeService {

  /**
   * Toma el tema de la respuesta del login y lo aplica.
   * Acepta resp.TEMA o resp.S00ARC.TEMA.
   *
   * Si la respuesta no trae tema:
   *  - con la predeterminada activa, aplica los que devuelva el servicio;  (retirado)
   *  - si limpiarSiNoViene = true, vuelve a los colores originales del
   *    sistema (evita que quede el tema de otra compañía guardado en el navegador);
   *  - si no, conserva el tema que ya está aplicado.
   */
  aplicarDesdeLogin(resp: any, limpiarSiNoViene: boolean = true): void {
    const tema: Tema | null = resp?.TEMA ?? resp?.S00ARC?.TEMA ?? null;

    if (this.esValido(tema)) {
      this.aplicar(tema);
    // mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
    // Se retira el tema de prueba (predeterminado)
    // } else if (USAR_TEMA) {
    //   this.aplicar(TEMA);
    } else if (limpiarSiNoViene) {
      this.limpiar();
    }
  }

  // mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
  /**
   * Aplica el tema que llega como texto "#PRIMARIO,#CLARO,#MUY_CLARO"
   * (servicio PermisoInicioSesion, campo TEMA).
   * Si llega "###" (sin parametrizar), vacío o con formato inválido,
   * vuelve a los colores originales del sistema.
   */
  aplicarDesdeTexto(valor: string): void {
    const partes = (valor || '').replace(/\s/g, '').split(',');
    const tema: Tema = { PRIMARIO: partes[0], CLARO: partes[1], MUY_CLARO: partes[2] };

    if (partes.length === 3 && this.esValido(tema)) {
      this.aplicar(tema);
    } else {
      this.limpiar();
    }
  }

  /** Aplica un tema a todo el sitio y lo guarda para la próxima carga. */
  aplicar(tema: Tema): void {
    if (!this.esValido(tema)) return;

    const marca     = tema.PRIMARIO.toLowerCase();          // color tal cual llega
    const primario  = this.suavizar(marca);                 // color cómodo para superficies grandes
    const claro     = tema.CLARO.toLowerCase();
    const seleccion = tema.MUY_CLARO.toLowerCase();
    const oscuro    = this.mezclar(primario, '#000000', 0.22);
    const masOscuro = this.mezclar(primario, '#000000', 0.45);
    const hover     = this.mezclar(primario, '#000000', 0.15);

    // Superficies NEUTRAS: grises con apenas un toque del color principal.
    // Así los fondos, tarjetas y filas no se tiñen aunque los colores sean saturados.
    const muyClaro  = this.mezclar('#ffffff', primario, 0.07);   // fondos de tarjetas, grillas, paneles
    const borde     = this.mezclar('#ffffff', primario, 0.13);   // bordes y separadores
    const fondo     = this.mezclar('#f7f8fa', primario, 0.03);   // fondo general de la aplicación
    // Grises claros del home, tabs y grillas. En el CSS se usan como
    // var(--app-superficie, #gris-original): sin tema conservan su gris original.
    const superficie = this.mezclar('#f4f5f7', primario, 0.05);

    // Menú y barras oscuras: siempre oscuros (aunque el principal sea claro)
    const menu       = this.mezclar(marca, '#12141c', 0.30);
    const menuOscuro = this.mezclar(marca, '#12141c', 0.45);

    // Tarjetas oscuras del home del estudiante y la barra de novedades del login.
    // En el CSS se usan con su gris original como respaldo: var(--app-pizarra, #64748b)
    const pizarra   = primario;
    const pizarra2  = this.mezclar(primario, menu, 0.5);
    const superficie2 = this.mezclar('#d3d8df', primario, 0.10);

    // mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
    // Filas alternas de las grillas: tinte suave del color de acento (FILA_TINTE),
    // con la misma intensidad que tenía el gris original.
    // Si el acento es rojo (UAM, CUC) se usa el PRIMARIO para no dar filas rosadas.
    const fila = this.esRojizo(claro)
      ? this.mezclar('#ffffff', primario, FILA_TINTE)
      : this.mezclar('#ffffff', claro, FILA_TINTE);

    const variables: Record<string, string> = {
      '--app-primario':         primario,
      '--app-claro':            claro,
      '--app-muy-claro':        muyClaro,
      '--app-oscuro':           oscuro,
      '--app-mas-oscuro':       masOscuro,
      '--app-borde':            borde,
      '--app-seleccion':        seleccion,
      '--app-fondo':            fondo,
      '--app-superficie':       superficie,
      '--app-superficie-2':     superficie2,
      '--app-pizarra':          pizarra,
      '--app-pizarra-2':        pizarra2,
      '--app-pizarra-3':        menu,
      '--app-pizarra-4':        menuOscuro,
      '--app-menu':             menu,
      '--app-menu-oscuro':      menuOscuro,
      // mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
      '--app-fila':             fila,

      '--app-primario-rgb':     this.rgb(primario),
      '--app-claro-rgb':        this.rgb(claro),
      '--app-muy-claro-rgb':    this.rgb(muyClaro),
      '--app-oscuro-rgb':       this.rgb(oscuro),
      '--app-mas-oscuro-rgb':   this.rgb(masOscuro),
      '--app-borde-rgb':        this.rgb(borde),
      '--app-seleccion-rgb':    this.rgb(seleccion),

      '--app-texto-primario':   this.textoSobre(primario),
      '--app-texto-claro':      this.textoSobre(claro),
      '--app-texto-seleccion':  this.textoSobre(seleccion),

      '--app-bs-primario':       primario,
      '--app-bs-primario-rgb':   this.rgb(primario),
      '--app-bs-primario-hover': hover,
      '--app-bs-texto':          this.textoSobre(primario),
    };

    const root = document.documentElement.style;
    Object.keys(variables).forEach(k => root.setProperty(k, variables[k]));
    document.documentElement.setAttribute('data-tema', '1');   // activa las reglas de _tema.scss

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tema));
      // index.html lo lee para pintar el tema antes de que cargue Angular (evita parpadeo)
      localStorage.setItem(STORAGE_KEY + '_vars', JSON.stringify(variables));
    } catch (e) { }
  }

  /** Reaplica el último tema guardado (al recargar la página). */
  restaurar(): void {
    try {
      const guardado = localStorage.getItem(STORAGE_KEY);
      if (guardado) this.aplicar(JSON.parse(guardado));
    } catch (e) { }
  }

  /** Vuelve a los colores originales del sistema (sin tema). */
  limpiar(): void {
    const root = document.documentElement.style;
    for (let i = root.length - 1; i >= 0; i--) {
      const prop = root[i];
      if (prop.startsWith('--app-')) root.removeProperty(prop);
    }
    document.documentElement.removeAttribute('data-tema');
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY + '_vars');
    } catch (e) { }
  }

  /** Devuelve el valor actual de un color del tema (útil para gráficos en TS). */
  color(nombre: 'primario' | 'claro' | 'muy-claro' | 'oscuro' | 'mas-oscuro' | 'borde' | 'seleccion' | 'menu'): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--app-' + nombre).trim();
  }

  // ---------------------------------------------------------------------------

  private esValido(tema: any): tema is Tema {
    return !!tema && HEX.test(tema.PRIMARIO) && HEX.test(tema.CLARO) && HEX.test(tema.MUY_CLARO);
  }

  private aRgb(hex: string): number[] {
    return [1, 3, 5].map(i => parseInt(hex.substr(i, 2), 16));
  }

  private rgb(hex: string): string {
    return this.aRgb(hex).join(', ');
  }

  /** Mezcla dos colores: peso = proporción del segundo color (0..1). */
  private mezclar(a: string, b: string, peso: number): string {
    const ca = this.aRgb(a), cb = this.aRgb(b);
    return '#' + ca.map((v, i) => Math.round(v * (1 - peso) + cb[i] * peso)
      .toString(16).padStart(2, '0')).join('');
  }

  /** Ajusta luz y saturación del color a un rango cómodo, conservando el tono. */
  private suavizar(hex: string): string {
    const [r, g, b] = this.aRgb(hex).map(v => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    let l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? ((b - r) / d + 2) : ((r - g) / d + 4);
      h /= 6;
    }
    l = Math.min(Math.max(l, PRIMARIO_LUZ_MIN), PRIMARIO_LUZ_MAX);
    s = Math.min(s, PRIMARIO_SAT_MAX);

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const canal = (t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const rgb = s === 0 ? [l, l, l] : [canal(h + 1 / 3), canal(h), canal(h - 1 / 3)];
    return '#' + rgb.map(v => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
  }

  // mospina 10-10-16 EPT-2380 Cambio general identidad AVATAR
  /** true si el color es rojo/rosado (tono entre 330° y 20°, con saturación). */
  private esRojizo(hex: string): boolean {
    const [r, g, b] = this.aRgb(hex).map(v => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    if (d < 0.15 || max !== r) return false;          // gris o no domina el rojo
    let h = ((g - b) / d) * 60;
    if (h < 0) h += 360;
    return h <= 20 || h >= 330;
  }

  /** Blanco o casi negro según el contraste con el fondo. */
  private textoSobre(fondo: string): string {
    const [r, g, b] = this.aRgb(fondo);
    const luminancia = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminancia < 165 ? '#ffffff' : '#212529';
  }
}